// src/pages/Admin/Payments.jsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Menu,
  MenuItem,
  Avatar,
  Badge,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Alert,
  LinearProgress,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Search,
  FilterList,
  Refresh,
  MoreVert,
  Visibility,
  CheckCircle,
  Cancel,
  AttachMoney,
  Receipt,
  Download,
  PictureAsPdf,
  Image,
  Chat,
  Email,
  Phone,
  ExpandMore,
  Warning,
  Paid,
  Payment,
  History,
  ArrowForward,
  FileCopy,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import { formatCurrency, formatDate, formatDateTime, getStatusColor } from '../../utils/helpers';
import toast from 'react-hot-toast';

const AdminPayments = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');
  const [tabValue, setTabValue] = useState(0);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [verifyDialog, setVerifyDialog] = useState(null);
  const [refundDialog, setRefundDialog] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedPayment, setExpandedPayment] = useState(null);

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllPayments();
      setPayments(response.data);
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    const statusMap = ['all', 'pending', 'verified', 'failed', 'refunded'];
    setStatusFilter(statusMap[newValue]);
  };

  const handleVerifyPayment = async (id, action) => {
    try {
      await adminAPI.verifyPayment(id, { action, verifiedBy: 'admin' });
      toast.success(`Payment ${action === 'approve' ? 'approved' : 'rejected'}`);
      fetchPayments();
      setVerifyDialog(null);
    } catch (error) {
      console.error('Error verifying payment:', error);
      toast.error('Failed to verify payment');
    }
  };

  const handleRefund = async (id, refundAmount) => {
    try {
      await adminAPI.refundPayment(id, { refundAmount, reason: 'Customer request' });
      toast.success('Refund processed successfully');
      fetchPayments();
      setRefundDialog(null);
    } catch (error) {
      console.error('Error processing refund:', error);
      toast.error('Failed to process refund');
    }
  };

  const handleDownloadReceipt = (payment) => {
    // Implement receipt download
    toast.success('Downloading receipt...');
  };

  const stats = {
    total: payments.length,
    totalAmount: payments.reduce((sum, p) => sum + p.amount, 0),
    pending: payments.filter(p => p.status === 'pending').length,
    pendingAmount: payments.filter(p => p.status === 'pending').reduce((sum, p) => sum + p.amount, 0),
    verified: payments.filter(p => p.status === 'verified').length,
    verifiedAmount: payments.filter(p => p.status === 'verified').reduce((sum, p) => sum + p.amount, 0),
    failed: payments.filter(p => p.status === 'failed').length,
    refunded: payments.filter(p => p.status === 'refunded').length,
  };

  const filteredPayments = payments.filter(payment => {
    const matchesSearch = 
      payment.enquiryId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.buyerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.transactionId?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || payment.status === statusFilter;
    const matchesMethod = paymentMethodFilter === 'all' || payment.method === paymentMethodFilter;
    return matchesSearch && matchesStatus && matchesMethod;
  });

  if (loading) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <LinearProgress sx={{ mb: 2 }} />
        <Typography>Loading payments...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Payments Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage and verify all payment transactions
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchPayments}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Download />}
          >
            Export Report
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Total Payments
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.total}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#2196f315', color: '#2196f3' }}>
                  <Payment />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Total Amount
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {formatCurrency(stats.totalAmount)}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#4caf5015', color: '#4caf50' }}>
                  <AttachMoney />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Pending Verification
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {stats.pending}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {formatCurrency(stats.pendingAmount)}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#ff980015', color: '#ff9800' }}>
                  <Warning />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Verified Payments
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {stats.verified}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {formatCurrency(stats.verifiedAmount)}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#4caf5015', color: '#4caf50' }}>
                  <CheckCircle />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="All" icon={<Payment />} iconPosition="start" />
          <Tab 
            label="Pending" 
            icon={
              <Badge badgeContent={stats.pending} color="warning">
                <Warning />
              </Badge>
            } 
            iconPosition="start" 
          />
          <Tab label="Verified" icon={<CheckCircle />} iconPosition="start" />
          <Tab label="Failed" icon={<Cancel />} iconPosition="start" />
          <Tab label="Refunded" icon={<History />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Search and Filters */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search by enquiry ID, buyer, or transaction ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Payment Method</InputLabel>
              <Select
                label="Payment Method"
                value={paymentMethodFilter}
                onChange={(e) => setPaymentMethodFilter(e.target.value)}
              >
                <MenuItem value="all">All Methods</MenuItem>
                <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                <MenuItem value="credit_card">Credit Card</MenuItem>
                <MenuItem value="paypal">PayPal</MenuItem>
                <MenuItem value="swift">SWIFT</MenuItem>
                <MenuItem value="cryptocurrency">Cryptocurrency</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Date Range</InputLabel>
              <Select label="Date Range" defaultValue="all">
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="week">This Week</MenuItem>
                <MenuItem value="month">This Month</MenuItem>
                <MenuItem value="quarter">This Quarter</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              startIcon={<FilterList />}
              variant="outlined"
              onClick={() => {}}
            >
              More Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Payments Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">Transaction</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">Buyer</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">Amount</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">Method</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">Status</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">Date</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">Actions</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPayments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Payment sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                        No payments found
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPayments.map((payment) => (
                  <React.Fragment key={payment._id}>
                    <TableRow hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {payment.transactionId}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Enquiry: #{payment.enquiryId}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 36, height: 36 }}>
                            {payment.buyerName?.charAt(0) || 'B'}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight="medium">
                              {payment.buyerName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {payment.country}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(payment.amount)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {payment.currency}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={payment.method}
                          size="small"
                          variant="outlined"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={payment.status}
                          size="small"
                          color={getStatusColor(payment.status)}
                          sx={{ fontWeight: 'medium' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(payment.paymentDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {payment.status === 'pending' && (
                            <>
                              <Tooltip title="Approve">
                                <IconButton
                                  size="small"
                                  onClick={() => handleVerifyPayment(payment._id, 'approve')}
                                  color="success"
                                >
                                  <CheckCircle />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject">
                                <IconButton
                                  size="small"
                                  onClick={() => handleVerifyPayment(payment._id, 'reject')}
                                  color="error"
                                >
                                  <Cancel />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => setExpandedPayment(expandedPayment === payment._id ? null : payment._id)}
                            >
                              <ExpandMore 
                                sx={{ 
                                  transform: expandedPayment === payment._id ? 'rotate(180deg)' : 'none',
                                  transition: 'transform 0.2s'
                                }}
                              />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Download Receipt">
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadReceipt(payment)}
                              color="primary"
                            >
                              <Download />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* Expanded Details */}
                    {expandedPayment === payment._id && (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ bgcolor: 'grey.50', p: 3 }}>
                          <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                              <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                  Payment Details
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">
                                      Bank Name:
                                    </Typography>
                                    <Typography variant="body2">
                                      {payment.bankName || 'N/A'}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">
                                      Account Number:
                                    </Typography>
                                    <Typography variant="body2">
                                      {payment.accountNumber || 'N/A'}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">
                                      SWIFT/BIC:
                                    </Typography>
                                    <Typography variant="body2">
                                      {payment.swiftCode || 'N/A'}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">
                                      Reference:
                                    </Typography>
                                    <Typography variant="body2">
                                      {payment.reference || 'N/A'}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Paper>
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                  Payment Proof
                                </Typography>
                                {payment.proofImage ? (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <img
                                      src={payment.proofImage}
                                      alt="Payment Proof"
                                      style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover' }}
                                    />
                                    <Button
                                      size="small"
                                      startIcon={<Image />}
                                      onClick={() => window.open(payment.proofImage, '_blank')}
                                    >
                                      View Full Image
                                    </Button>
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    No proof uploaded
                                  </Typography>
                                )}
                              </Paper>
                            </Grid>

                            <Grid item xs={12}>
                              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                                {payment.status === 'verified' && (
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={<History />}
                                    onClick={() => setRefundDialog(payment)}
                                  >
                                    Process Refund
                                  </Button>
                                )}
                                <Button
                                  variant="contained"
                                  startIcon={<Receipt />}
                                  onClick={() => handleDownloadReceipt(payment)}
                                >
                                  Generate Receipt
                                </Button>
                              </Box>
                            </Grid>
                          </Grid>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Verify Payment Dialog */}
      <Dialog
        open={Boolean(verifyDialog)}
        onClose={() => setVerifyDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Verify Payment</DialogTitle>
        <DialogContent>
          {verifyDialog && (
            <>
              <DialogContentText gutterBottom>
                Verify payment for <strong>{verifyDialog.buyerName}</strong>
              </DialogContentText>
              <Box sx={{ my: 2 }}>
                <Alert severity="info">
                  Amount: {formatCurrency(verifyDialog.amount)}
                  <br />
                  Method: {verifyDialog.method}
                  <br />
                  Transaction ID: {verifyDialog.transactionId}
                </Alert>
              </Box>
              {verifyDialog.proofImage && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Payment Proof:
                  </Typography>
                  <img
                    src={verifyDialog.proofImage}
                    alt="Payment Proof"
                    style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }}
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifyDialog(null)}>Cancel</Button>
          <Button 
            onClick={() => handleVerifyPayment(verifyDialog?._id, 'reject')}
            color="error"
          >
            Reject
          </Button>
          <Button 
            onClick={() => handleVerifyPayment(verifyDialog?._id, 'approve')}
            color="success"
            variant="contained"
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog
        open={Boolean(refundDialog)}
        onClose={() => setRefundDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Process Refund</DialogTitle>
        <DialogContent>
          {refundDialog && (
            <>
              <DialogContentText gutterBottom>
                Process refund for payment ID: <strong>{refundDialog.transactionId}</strong>
              </DialogContentText>
              <Box sx={{ my: 3 }}>
                <TextField
                  fullWidth
                  label="Refund Amount"
                  type="number"
                  defaultValue={refundDialog.amount}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Box>
              <TextField
                fullWidth
                label="Refund Reason"
                multiline
                rows={3}
                defaultValue="Customer request"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundDialog(null)}>Cancel</Button>
          <Button 
            onClick={() => handleRefund(refundDialog?._id, refundDialog?.amount)}
            color="error"
            variant="contained"
          >
            Process Refund
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminPayments;